import React, { useState, useRef, useEffect } from 'react';
import { transcribeAudio } from '../services/whisperApi';
import { localScore } from '../utils/textSimilarity';
import { saveRecord } from '../utils/storage';
import { saveLog } from '../services/readingLogsApi';
import { trackEvent } from '../utils/analytics';
import { createRecorder } from '../utils/audioRecording';

export default function RenshyuMode({ text, onComplete, onBack, childId, grade }) {
  const paragraphs = text.paragraphs ?? [text.content ?? []];

  // 1段落しかない短いテキストは選択画面をスキップ
  const skipSelect = paragraphs.length === 1;

  // フェーズ: select → recording → result
  const [phase, setPhase] = useState(skipSelect ? 'recording' : 'select');
  // 1段落テキストは最初から全選択
  const [rangeStart, setRangeStart] = useState(skipSelect ? 0 : null);
  const [rangeEnd, setRangeEnd]     = useState(skipSelect ? 0 : null);
  const [recording, setRecording]   = useState(false);
  const [loading, setLoading]       = useState(false);
  const [result, setResult]         = useState(null);
  const [audioUrl, setAudioUrl]     = useState(null);
  const [playing, setPlaying]       = useState(false);

  const mediaRef    = useRef(null);
  const chunksRef   = useRef([]);
  const audioRef    = useRef(null);
  const recStartRef = useRef(null);

  useEffect(() => {
    return () => { audioRef.current?.pause(); };
  }, []);

  // ── 段落タップ ────────────────────────────────────────────
  const handleParaTap = (idx) => {
    if (rangeStart === null) {
      // 1回目タップ → start確定
      setRangeStart(idx);
      setRangeEnd(null);
    } else if (rangeEnd === null) {
      if (idx === rangeStart) {
        // 同じ段落を2回タップ → 1段落だけ選択確定
        setRangeEnd(idx);
      } else if (idx > rangeStart) {
        // 後ろをタップ → end確定
        setRangeEnd(idx);
      } else {
        // 前をタップ → startを更新
        setRangeStart(idx);
      }
    } else {
      // 選択済みの状態でタップ → リセットして新しいstart
      setRangeStart(idx);
      setRangeEnd(null);
    }
  };

  // 選択された段落のテキストを結合
  const start = rangeStart ?? 0;
  const end   = rangeEnd   ?? rangeStart ?? 0;
  const selectedParagraphs = paragraphs.slice(start, end + 1);
  const selectedText       = selectedParagraphs.flat().join('');
  const isReady = rangeStart !== null && rangeEnd !== null;

  // ── 録音 ────────────────────────────────────────────────

  const startRecording = async () => {
    setResult(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr     = createRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = handleStop;
      mr.start();
      mediaRef.current    = mr;
      recStartRef.current = Date.now();
      trackEvent('ondoku_recording_start', {
        mode: 'renshyu',
        text_title: text.title || '（タイトルなし）',
        grade: grade ?? 'unknown',
        paragraph_count: paragraphs.length,
      });
      setRecording(true);
    } catch {
      alert('マイクが　つかえません。\nブラウザの　せってい　を　かくにんしてね。');
    }
  };

  const stopRecording = () => {
    mediaRef.current?.stop();
    mediaRef.current?.stream.getTracks().forEach((t) => t.stop());
    setRecording(false);
  };

  const handleStop = async () => {
    const duration_sec = recStartRef.current
      ? Math.round((Date.now() - recStartRef.current) / 1000)
      : 0;
    trackEvent('ondoku_recording_stop', { mode: 'renshyu', duration_sec, grade: grade ?? 'unknown' });
    setLoading(true);
    try {
      const mimeType = mediaRef.current?.mimeType || 'audio/webm';
      const blob = new Blob(chunksRef.current, { type: mimeType });
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      setAudioUrl(URL.createObjectURL(blob));
      setPlaying(false);
      const transcript = await transcribeAudio(blob);
      const { score, stars, feedback, display } = localScore(selectedText, transcript);
      setResult({ transcript, score, stars, feedback, display });
      setPhase('result');
    } catch (e) {
      console.error(e);
      setResult({ transcript: `エラー: ${e.message}`, score: 0, stars: 0, feedback: 'もういちど　ためしてね' });
      setPhase('result');
    }
    setLoading(false);
  };

  const handlePlay = () => {
    if (!audioUrl) return;
    if (playing) {
      audioRef.current?.pause();
      setPlaying(false);
    } else {
      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      audio.play();
      audio.onended = () => setPlaying(false);
      setPlaying(true);
    }
  };

  const handleSaveAndFinish = () => {
    const record = {
      mode: 'renshyu',
      title: text.title,
      totalStars: result?.stars ?? 0,
      avgScore: result?.score ?? 0,
      date: new Date().toISOString(),
    };
    const saved = saveRecord(record, childId);
    saveLog(saved, childId);
    onComplete({ ...record, id: saved.id });
  };

  const starStr = (n) => '⭐'.repeat(Math.max(0, n)) + '☆'.repeat(Math.max(0, 3 - n));

  // ── 段落選択画面 ─────────────────────────────────────────
  if (phase === 'select') {
    return (
      <div className="screen renshyu-screen">
        <div className="renshyu-header">
          <button className="btn-back" onClick={onBack}>← もどる</button>
          <div className="renshyu-title-wrap">
            <span className="renshyu-title-label">📖 れんしゅう</span>
            <span className="renshyu-title-book">『{text.title}』</span>
          </div>
        </div>

        <div style={{ padding: '10px 12px 4px' }}>
          {/* ぜんぶよむ ショートカット */}
          <button
            onClick={() => { setRangeStart(0); setRangeEnd(paragraphs.length - 1); setPhase('recording'); }}
            style={{
              width: '100%', padding: '11px', borderRadius: 14,
              border: '2px solid var(--purple)', background: 'var(--purple-bg)',
              color: 'var(--purple)', fontSize: 15, fontWeight: 800,
              fontFamily: 'var(--font)', cursor: 'pointer', marginBottom: 8,
            }}
          >
            📖 ぜんぶ　よむ（だんらく1〜{paragraphs.length}）
          </button>
          <div style={{ color: 'var(--text-sub)', fontSize: 13 }}>
            {rangeStart === null
              ? 'または　よみたい　さいしょの　だんらくを　えらんでね'
              : rangeEnd === null
              ? `だんらく${rangeStart + 1}から　→　さいごの　だんらくを　えらんでね`
              : `だんらく${start + 1}〜${end + 1}を　よむ　よ！`
            }
          </div>
        </div>

        {/* 段落一覧 */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '4px 12px 120px' }}>
          {paragraphs.map((para, idx) => {
            const inRange   = rangeStart !== null && rangeEnd !== null && idx >= start && idx <= end;
            const isStart   = idx === rangeStart;
            const isEndPara = idx === rangeEnd;
            const isPending = rangeStart !== null && rangeEnd === null && idx === rangeStart;
            return (
              <div
                key={idx}
                onClick={() => handleParaTap(idx)}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  padding: '10px 12px',
                  marginBottom: 6,
                  borderRadius: 14,
                  background: inRange
                    ? 'var(--purple-bg)'
                    : 'white',
                  border: `2px solid ${inRange || isPending ? 'var(--purple)' : '#e8e0f4'}`,
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
              >
                <span style={{
                  flexShrink: 0,
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  background: inRange || isPending ? 'var(--purple)' : '#e8e0f4',
                  color: inRange || isPending ? 'white' : 'var(--text-sub)',
                  fontSize: 13,
                  fontWeight: 800,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  {idx + 1}
                </span>
                <span style={{
                  fontSize: 14,
                  color: inRange ? 'var(--purple)' : 'var(--text)',
                  fontWeight: inRange ? 700 : 400,
                  lineHeight: 1.6,
                  flex: 1,
                }}>
                  {para.join('')}
                </span>
                {(isStart && rangeEnd !== null) && (
                  <span style={{ fontSize: 11, color: 'var(--purple)', fontWeight: 800 }}>ここから</span>
                )}
                {(isEndPara && rangeEnd !== null) && (
                  <span style={{ fontSize: 11, color: 'var(--purple)', fontWeight: 800 }}>ここまで</span>
                )}
              </div>
            );
          })}
        </div>

        {/* 決定ボタン */}
        {isReady && (
          <div style={{
            position: 'fixed', bottom: 0, left: 0, right: 0,
            padding: '12px 16px 24px',
            background: 'white',
            borderTop: '1px solid #e8e0f4',
          }}>
            <button
              onClick={() => setPhase('recording')}
              style={{
                width: '100%',
                padding: '16px',
                borderRadius: 18,
                border: 'none',
                background: 'linear-gradient(135deg, #7c3aed, #a855f7)',
                color: 'white',
                fontSize: 17,
                fontWeight: 900,
                fontFamily: 'var(--font)',
                cursor: 'pointer',
              }}
            >
              だんらく {start + 1}{start !== end ? `〜${end + 1}` : ''} を　よむ！ 🎤
            </button>
          </div>
        )}
      </div>
    );
  }

  // ── 録音画面 ─────────────────────────────────────────────
  if (phase === 'recording') {
    const allLines   = selectedParagraphs.flat();
    const isShortText = allLines.length <= 10;
    return (
      <div className="screen renshyu-screen">
        <div className="renshyu-header">
          <button className="btn-back" onClick={() => { stopRecording(); setPhase('select'); }}>← もどる</button>
          <div className="renshyu-title-wrap">
            <span className="renshyu-title-label">📖 れんしゅう</span>
            <span className="renshyu-title-book">
              だんらく {start + 1}{start !== end ? '〜' + (end + 1) : ''}
</span>
          </div>
        </div>

        {/* 選択した段落のテキスト */}
        <div className={`reading-box${isShortText ? ' short-reading-box' : ''}`}>
          <p className="reading-label">よむ　ところ：</p>
          <div className={`reading-context${isShortText ? ' short-reading-context' : ''}`}>
            {allLines.map((line, i) => (
              <div key={i} className="reading-col current">{line}</div>
            ))}
          </div>
        </div>

        {/* 録音ボタン */}
        {!loading && (
          <button
            className={`btn-record ${recording ? 'recording' : ''}`}
            onClick={recording ? stopRecording : startRecording}
          >
            {recording ? (
              <><span className="rec-dot" />よんでる…　おわったら　おす</>
            ) : (
              <><span className="mic-icon">🎤</span>ここを　おして　よむ</>
            )}
          </button>
        )}

        {loading && (
          <div className="loading-box">
            <div className="spinner" />
            <p>はんていちゅう…</p>
          </div>
        )}
      </div>
    );
  }

  // ── 結果画面 ─────────────────────────────────────────────
  return (
    <div className="screen renshyu-screen">
      <div className="renshyu-header">
        <button className="btn-back" onClick={onBack}>← もどる</button>
        <div className="renshyu-title-wrap">
          <span className="renshyu-title-label">📖 れんしゅう</span>
          <span className="renshyu-title-book">『{text.title}』</span>
        </div>
      </div>

      <div className="result-box">
        <div className="result-stars">{starStr(result?.stars ?? 0)}</div>
        <div className="result-score">{result?.score ?? 0}てん</div>
        <div className="result-transcript">
          <span className="result-label">きこえた　ことば：</span>
          <span>{result?.display || result?.transcript}</span>
        </div>
        <div className="result-feedback">{result?.feedback}</div>

        {audioUrl && (
          <button className="btn-play" onClick={handlePlay}>
            {playing ? '⏹ とめる' : '▶️ きいてみる'}
          </button>
        )}

        <div className="result-actions">
          <button className="btn-retry" onClick={() => { setPhase('recording'); setResult(null); }}>
            🔄 もういちど
          </button>
          {!skipSelect && (
            <button className="btn-retry" onClick={() => { setPhase('select'); setResult(null); setRangeStart(null); setRangeEnd(null); }}>
              📋 だんらくを　えらびなおす
            </button>
          )}
          <button className="btn-next" onClick={handleSaveAndFinish}>
            🏁 おわる
          </button>
        </div>
      </div>
    </div>
  );
}
